import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';
import Logo from './Logo';
import media from '../style/media';
import { Flex } from './shared/Flex';
import { LayoutDashboard, Scan, Heart, ShieldCheck, Settings, LogOut, Menu, X } from 'lucide-react';
import { StorageKeys } from '../utils/storage';

const Wrapper = styled(Flex)`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  justify-content: space-between;
  align-items: center;
  min-height: 80px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding: 0 1.5rem;
  box-sizing: border-box;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);

  ${media.tablet`
    padding: 0 3.5rem;
  `}
`;

const NavItems = styled.div`
  display: none;
  
  ${media.tablet`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `}
`;

const NavItem = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: ${({ isActive }) => isActive ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${({ isActive }) => isActive ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const LogoutButton = styled(NavItem)`
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.3);

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.4);
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 0.5rem;
`;

// Mobile menu button
const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  ${media.tablet`
    display: none;
  `}
`;

// Mobile dropdown
const MobileDropdown = styled.div<{ isOpen: boolean; isClosing: boolean }>`
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  position: fixed;
  top: 80px;
  left: 1.5rem;
  right: 1.5rem;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  overflow: hidden;
  animation: ${({ isClosing }) => isClosing ? 'slideUp' : 'slideDown'} 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }

  ${media.tablet`
    display: none;
  `}
`;

const MobileMenuList = styled.div`
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MobileMenuItem = styled.button<{ isActive?: boolean; variant?: 'default' | 'danger' }>`
  width: 100%;
  padding: 0.875rem 1rem;
  background: ${({ isActive, variant }) =>
    variant === 'danger' ? 'rgba(239, 68, 68, 0.1)' :
      isActive ? 'rgba(102, 126, 234, 0.15)' : 'transparent'};
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ variant }) => variant === 'danger' ? '#dc2626' : '#0f172a'};
  font-size: 0.9375rem;
  font-weight: 600;
  text-align: left;

  &:hover {
    background: ${({ variant }) =>
    variant === 'danger' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(102, 126, 234, 0.1)'};
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    flex-shrink: 0;
    opacity: 0.8;
  }
`;

const MobileDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 0.5rem 0;
`;

const Backdrop = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 9998;

  ${media.tablet`
    display: none;
  `}
`;

const TopBar = ({ onSettingsClick, isMeasuring }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const pid = useMemo(() => localStorage.getItem(StorageKeys.PATIENT_ID) || '', []);
  const isPidValid = pid && pid !== 'null';
  const orgId = useMemo(() => localStorage.getItem(StorageKeys.ORG_ID) || '', []);
  const isOrgIdValid = orgId && orgId !== 'null';

  const user = useMemo(() => {
    const userStr = localStorage.getItem(StorageKeys.AUTH_USER);
    return userStr ? JSON.parse(userStr) : null;
  }, []);
  const isOrgType3 = user?.org_type === 3;

  const isActive = (path: string) => location.pathname === path;

  const handleMobileMenuToggle = useCallback(() => {
    if (isMobileMenuOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsMobileMenuOpen(false);
        setIsClosing(false);
      }, 250);
    } else {
      setIsMobileMenuOpen(true);
    }
  }, [isMobileMenuOpen]);

  const handleMobileNavigate = useCallback((path: string) => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
      navigate(path);
    }, 100);
  }, [navigate]);

  const handleMobileLogout = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
      logout();
    }, 100);
  }, [logout]);

  return (
    <>
      <Wrapper>
        <Logo />

        {/* Desktop Navigation */}
        <NavItems>
          <NavItem
            isActive={isActive('/dashboard')}
            onClick={() => navigate('/dashboard')}
          >
            <LayoutDashboard />
            <span>Dashboard</span>
          </NavItem>

          {isOrgIdValid && isOrgType3 && (
            <NavItem
              isActive={isActive('/fullScan')}
              onClick={() => navigate('/fullScan')}
            >
              <Scan />
              <span>Full Scan</span>
            </NavItem>
          )}

          {(isPidValid || (isOrgIdValid && isOrgType3)) && (
            <>
              <NavItem
                isActive={isActive('/vitals')}
                onClick={() => navigate('/vitals')}
              >
                <Heart />
                <span>Vitals</span>
              </NavItem>

              <NavItem
                isActive={isActive('/licenseInfo')}
                onClick={() => navigate('/licenseInfo')}
              >
                <ShieldCheck />
                <span>License</span>
              </NavItem>
            </>
          )}

          {isPidValid && (
            <NavItem
              isActive={isActive('/scan')}
              onClick={() => navigate('/scan')}
            >
              <Scan />
              <span>Scan</span>
            </NavItem>
          )}

          <NavItem
            isActive={isActive('/settings')}
            onClick={() => navigate('/settings')}
          >
            <Settings />
            <span>Settings</span>
          </NavItem>

          <Divider />

          <LogoutButton onClick={logout}>
            <LogOut />
            <span>Sign Out</span>
          </LogoutButton>
        </NavItems>

        {/* Mobile Menu Button */}
        <MobileMenuButton onClick={handleMobileMenuToggle}>
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </MobileMenuButton>
      </Wrapper>

      {/* Mobile Dropdown Menu */}
      <Backdrop isOpen={isMobileMenuOpen} onClick={handleMobileMenuToggle} />
      <MobileDropdown isOpen={isMobileMenuOpen} isClosing={isClosing}>
        <MobileMenuList>
          <MobileMenuItem
            isActive={isActive('/dashboard')}
            onClick={() => handleMobileNavigate('/dashboard')}
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </MobileMenuItem>

          {isOrgIdValid && isOrgType3 && (
            <MobileMenuItem
              isActive={isActive('/fullScan')}
              onClick={() => handleMobileNavigate('/fullScan')}
            >
              <Scan size={18} />
              <span>Full Scan</span>
            </MobileMenuItem>
          )}

          {(isPidValid || (isOrgIdValid && isOrgType3)) && (
            <>
              <MobileMenuItem
                isActive={isActive('/vitals')}
                onClick={() => handleMobileNavigate('/vitals')}
              >
                <Heart size={18} />
                <span>{isOrgIdValid && isOrgType3 ? 'Vitals' : 'My Vitals'}</span>
              </MobileMenuItem>

              <MobileMenuItem
                isActive={isActive('/licenseInfo')}
                onClick={() => handleMobileNavigate('/licenseInfo')}
              >
                <ShieldCheck size={18} />
                <span>License Info</span>
              </MobileMenuItem>
            </>
          )}

          {isPidValid && (
            <MobileMenuItem
              isActive={isActive('/scan')}
              onClick={() => handleMobileNavigate('/scan')}
            >
              <Scan size={18} />
              <span>Start Scan</span>
            </MobileMenuItem>
          )}

          <MobileMenuItem
            isActive={isActive('/settings')}
            onClick={() => handleMobileNavigate('/settings')}
          >
            <Settings size={18} />
            <span>Settings</span>
          </MobileMenuItem>

          <MobileDivider />

          <MobileMenuItem variant="danger" onClick={handleMobileLogout}>
            <LogOut size={18} />
            <span>Sign Out</span>
          </MobileMenuItem>
        </MobileMenuList>
      </MobileDropdown>
    </>
  );
};

export default TopBar;
